import React from 'react';
import { createPortal } from 'react-dom';
import { Building2, X } from 'lucide-react';

import { AddressAutocompleteInput } from '../../components/AddressAutocompleteInput';
import { AppSelectDropdown } from '../../components/AppSelectDropdown';
import {
  Contact,
  CONTACT_KINDS,
  ContactKind,
  Organization,
  OrganizationType,
} from '../../types';
import { UI_Z_CLASS, UI_Z_INDEX } from '../../utils/uiZIndex';
import { OrganizationTypePicker } from './OrganizationTypePicker';
import { useDiscardGuard, useUserEdited } from '../../hooks/useDiscardGuard';
import {
  OrganizationRegistryFields,
  organizationRegistryPayload,
} from './OrganizationRegistryFields';

interface Props {
  onClose: () => void;
  onCreated: (contact: Contact) => void;
  /** When provided, pre-selects the organization (e.g. opened from an org detail page). */
  defaultOrganizationId?: string | null;
  /** Optional override of the initial kind (defaults to Privatkunde). */
  defaultKind?: ContactKind;
  /**
   * `drawer` = rechte Seitenleiste wie Plantafel / Neues Projekt.
   * `modal` = zentrierter Dialog (Standard für ältere Aufrufer).
   */
  variant?: 'modal' | 'drawer';
}

export const CreateContactModal: React.FC<Props> = ({
  onClose,
  onCreated,
  defaultOrganizationId,
  defaultKind = 'Privatkunde',
  variant = 'modal',
}) => {
  const isDrawer = variant === 'drawer';
  const [kind, setKind] = React.useState<ContactKind>(
    defaultOrganizationId ? 'Gewerblicher Kontakt' : defaultKind,
  );
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [organizationId, setOrganizationId] = React.useState<string | null>(
    defaultOrganizationId ?? null,
  );
  const [orgs, setOrgs] = React.useState<Organization[]>([]);
  const [createOrgInline, setCreateOrgInline] = React.useState(false);
  const [newOrgName, setNewOrgName] = React.useState('');
  const [newOrgType, setNewOrgType] = React.useState<OrganizationType>('Gewerblicher Kunde');
  const [newOrgAddress, setNewOrgAddress] = React.useState('');
  const [newOrgVatId, setNewOrgVatId] = React.useState('');
  const [newOrgTaxNumber, setNewOrgTaxNumber] = React.useState('');
  const [newOrgCommercialRegisterNumber, setNewOrgCommercialRegisterNumber] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const labelClass = isDrawer
    ? 'mb-1 block text-sm font-medium text-app-text-muted'
    : 'mb-1 block text-xs font-medium text-app-text-muted';
  const inputClass = isDrawer
    ? 'w-full rounded-md border border-app-border bg-app-surface px-3 py-2 text-sm outline-none focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue'
    : 'w-full px-3 py-2 bg-app-surface border border-app-border rounded text-sm';

  const resetNewOrgFields = React.useCallback(() => {
    setCreateOrgInline(false);
    setNewOrgName('');
    setNewOrgType('Gewerblicher Kunde');
    setNewOrgAddress('');
    setNewOrgVatId('');
    setNewOrgTaxNumber('');
    setNewOrgCommercialRegisterNumber('');
  }, []);

  React.useEffect(() => {
    if (kind !== 'Gewerblicher Kontakt') return;
    fetch('/api/organizations')
      .then((r) => (r.ok ? (r.json() as Promise<Organization[]>) : []))
      .then((rows) => setOrgs(Array.isArray(rows) ? rows : []))
      .catch(() => setOrgs([]));
  }, [kind]);

  React.useEffect(() => {
    if (kind !== 'Gewerblicher Kontakt') {
      setOrganizationId(null);
      resetNewOrgFields();
    }
  }, [kind, resetNewOrgFields]);

  // Klick daneben, Esc, ✕: nach eigenen Eingaben erst nachfragen.
  const { edited, editProps } = useUserEdited();
  const discardGuard = useDiscardGuard({
    dirty: edited,
    onClose,
    disabled: submitting,
    description: 'Der Kontakt ist noch nicht angelegt. Deine Eingaben gehen verloren.',
  });
  const requestClose = discardGuard.requestClose;

  React.useEffect(() => {
    if (!isDrawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Nested pickers close first; do not discard the drawer form.
      if (
        document.querySelector('[data-app-select-list]') ||
        document.querySelector('[data-app-select-panel]') ||
        document.querySelector('[data-org-type-picker-panel]') ||
        document.querySelector('[data-address-autocomplete-list]')
      ) {
        return;
      }
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDrawer, requestClose]);

  const startInlineOrgCreate = () => {
    setError(null);
    setOrganizationId(null);
    setCreateOrgInline(true);
    setNewOrgName('');
    setNewOrgType('Gewerblicher Kunde');
    setNewOrgAddress('');
    setNewOrgVatId('');
    setNewOrgTaxNumber('');
    setNewOrgCommercialRegisterNumber('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (kind === 'Privatkunde' && !lastName.trim()) {
      setError('Nachname ist für Privatkunden erforderlich.');
      return;
    }
    if (kind === 'Gewerblicher Kontakt' && createOrgInline && !newOrgName.trim()) {
      setError('Bitte einen Organisationsnamen eingeben.');
      return;
    }
    if (kind === 'Gewerblicher Kontakt' && !createOrgInline && !organizationId) {
      setError('Bitte eine Organisation auswählen oder eine neue anlegen.');
      return;
    }
    setSubmitting(true);
    setError(null);
    let createdInlineOrgId: string | null = null;
    try {
      let resolvedOrgId = organizationId;

      if (kind === 'Gewerblicher Kontakt' && createOrgInline) {
        const orgRes = await fetch('/api/organizations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: newOrgName.trim(),
            organization_type: newOrgType,
            registered_address: newOrgAddress.trim() || undefined,
            ...organizationRegistryPayload({
              vatId: newOrgVatId,
              taxNumber: newOrgTaxNumber,
              commercialRegisterNumber: newOrgCommercialRegisterNumber,
            }),
          }),
        });
        if (!orgRes.ok) {
          const body = (await orgRes.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? 'Organisation konnte nicht erstellt werden.');
        }
        const createdOrg = (await orgRes.json()) as Organization;
        createdInlineOrgId = createdOrg.id;
        resolvedOrgId = createdOrg.id;
      }

      const body: Record<string, unknown> = {
        contact_kind: kind,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        address: address.trim() || undefined,
      };
      if (kind === 'Gewerblicher Kontakt' && resolvedOrgId) {
        body.organization_id = resolvedOrgId;
        if (createOrgInline && newOrgName.trim()) {
          body.company = newOrgName.trim();
        }
      }
      const res = await fetch('/api/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'Kontakt konnte nicht erstellt werden.');
      }
      const created = (await res.json()) as Contact;

      if (kind === 'Gewerblicher Kontakt' && createOrgInline && resolvedOrgId && created.id != null) {
        await fetch(`/api/organizations/${encodeURIComponent(resolvedOrgId)}/primary-contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contact_id: created.id }),
        }).catch(() => {
          /* Kontakt ist angelegt — Primary optional */
        });
      }

      onCreated(created);
    } catch (err) {
      if (createdInlineOrgId) {
        await fetch(`/api/organizations/${encodeURIComponent(createdInlineOrgId)}`, {
          method: 'DELETE',
        }).catch(() => {
          /* Best-effort rollback if contact create failed after org create */
        });
      }
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler.');
    } finally {
      setSubmitting(false);
    }
  };

  const orgOptions = React.useMemo(
    () => [
      { value: '', label: '— Keine zuordnen —' },
      ...orgs.map((o) => ({ value: o.id, label: o.name })),
    ],
    [orgs],
  );

  const formBody = (
    <form
      id="create-contact-form"
      onSubmit={handleSubmit}
      className={isDrawer ? 'flex flex-col flex-1 min-h-0' : 'p-5 space-y-4'}
      {...editProps}
    >
      <div
        className={
          isDrawer
            ? 'app-scrollbar flex-1 space-y-4 overflow-y-auto overscroll-contain p-4'
            : 'space-y-4'
        }
      >
        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}
        <div className={isDrawer ? 'block text-sm' : 'space-y-1'}>
          <label className={labelClass}>Kontaktart</label>
          <div className="flex flex-wrap gap-1 rounded-lg border border-app-border bg-app-surface-muted/80 p-0.5">
            {CONTACT_KINDS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setKind(t)}
                className={`flex-1 min-w-[8rem] px-2 py-1.5 text-xs font-medium rounded-md transition-all ${
                  kind === t
                    ? 'bg-app-surface text-app-text shadow-sm ring-1 ring-slate-200/80'
                    : 'text-app-text-muted hover:bg-app-hover/80'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {kind === 'Gewerblicher Kontakt' && (
          <div className={isDrawer ? 'block text-sm' : 'space-y-1'}>
            <label className={labelClass}>Organisation</label>
            {createOrgInline ? (
              <div className="space-y-3 rounded-lg border border-app-border bg-app-surface-muted p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-app-text">Neue Organisation</p>
                  <button
                    type="button"
                    className="text-xs font-medium text-app-text-subtle hover:text-app-text"
                    onClick={resetNewOrgFields}
                  >
                    Abbrechen
                  </button>
                </div>
                <div className="block text-sm">
                  <label className={labelClass}>Typ</label>
                  <OrganizationTypePicker value={newOrgType} onChange={setNewOrgType} variant="default" />
                </div>
                <div className="block text-sm">
                  <label className={labelClass}>
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newOrgName}
                    onChange={(e) => setNewOrgName(e.target.value)}
                    className={inputClass}
                    placeholder="z. B. Bauunternehmung Becker GmbH"
                    autoFocus
                  />
                </div>
                <div className="block text-sm">
                  <label className={labelClass}>Firmensitz</label>
                  <AddressAutocompleteInput
                    value={newOrgAddress}
                    onChange={setNewOrgAddress}
                    inputClassName={inputClass}
                    placeholder="Straße, Hausnummer, PLZ Ort"
                  />
                </div>
                <OrganizationRegistryFields
                  values={{
                    vatId: newOrgVatId,
                    taxNumber: newOrgTaxNumber,
                    commercialRegisterNumber: newOrgCommercialRegisterNumber,
                  }}
                  onChange={(patch) => {
                    if (patch.vatId !== undefined) setNewOrgVatId(patch.vatId);
                    if (patch.taxNumber !== undefined) setNewOrgTaxNumber(patch.taxNumber);
                    if (patch.commercialRegisterNumber !== undefined) {
                      setNewOrgCommercialRegisterNumber(patch.commercialRegisterNumber);
                    }
                  }}
                  labelClass={labelClass}
                  inputClass={inputClass}
                  fieldWrapClass="block text-sm"
                />
                <p className="text-xs leading-snug text-app-text-subtle">
                  Organisation und Kontakt werden gemeinsam angelegt. Der Kontakt wird als
                  Ansprechpartner verknüpft.
                </p>
              </div>
            ) : (
              <AppSelectDropdown
                value={organizationId ?? ''}
                onChange={(v) => {
                  setOrganizationId(v || null);
                  resetNewOrgFields();
                }}
                options={orgOptions}
                searchable
                searchPlaceholder="Organisation suchen…"
                onCreate={startInlineOrgCreate}
                createLabel="Neue Organisation erstellen"
                createIcon={<Building2 className="h-4 w-4" />}
                menuZIndex={UI_Z_INDEX.portalDropdownInModal}
                className="w-full"
                triggerClassName={
                  isDrawer
                    ? 'w-full inline-flex h-auto min-h-0 items-center justify-between gap-2 rounded-md border border-app-border bg-app-surface px-3 py-2 text-left text-sm text-app-text hover:bg-app-hover'
                    : undefined
                }
              />
            )}
          </div>
        )}

        <div className={`grid gap-3 ${isDrawer ? 'grid-cols-1' : 'grid-cols-2'}`}>
          <div className={isDrawer ? 'block text-sm' : 'space-y-1'}>
            <label className={labelClass}>Vorname</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className={inputClass}
            />
          </div>
          <div className={isDrawer ? 'block text-sm' : 'space-y-1'}>
            <label className={labelClass}>
              Nachname {kind === 'Privatkunde' && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <div className={isDrawer ? 'block text-sm' : 'space-y-1'}>
          <label className={labelClass}>E-Mail</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className={isDrawer ? 'block text-sm' : 'space-y-1'}>
          <label className={labelClass}>Telefon</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className={isDrawer ? 'block text-sm' : 'space-y-1'}>
          <label className={labelClass}>Adresse</label>
          <AddressAutocompleteInput
            value={address}
            onChange={setAddress}
            inputClassName={inputClass}
          />
        </div>
      </div>

      {isDrawer ? (
        <div className="flex flex-wrap gap-2 border-t border-app-border/60 p-3">
          <button
            type="submit"
            disabled={submitting}
            className="btn-pipedrive-green flex-1 !py-2 disabled:opacity-50"
          >
            {submitting ? 'Erstellen …' : 'Anlegen'}
          </button>
        </div>
      ) : (
        <div className="pt-2 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-pipedrive-secondary">
            Abbrechen
          </button>
          <button type="submit" disabled={submitting} className="btn-pipedrive-green disabled:opacity-50">
            {submitting ? 'Erstellen …' : 'Erstellen'}
          </button>
        </div>
      )}
    </form>
  );

  if (isDrawer) {
    return createPortal(
      <>
        <div
          className={`fixed inset-0 ${UI_Z_CLASS.plantafelDrawer} flex justify-end bg-black/25`}
          role="presentation"
          {...discardGuard.backdropProps}
        >
          <div
            className="relative flex h-full w-[min(400px,100%)] flex-col border-l border-app-border bg-app-surface shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-contact-drawer-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-app-border/60 p-4">
              <h3 id="create-contact-drawer-title" className="truncate text-lg font-semibold text-app-text">
                Neuen Kontakt anlegen
              </h3>
              <button type="button" className="rounded p-1 text-app-text-subtle hover:bg-app-hover" onClick={requestClose}>
                <X className="h-4 w-4" />
              </button>
            </div>
            {formBody}
          </div>
        </div>
        {discardGuard.dialog}
      </>,
      document.body,
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 z-[220] flex items-center justify-center bg-black/40 p-4"
        {...discardGuard.backdropProps}
      >
        <div className="w-full max-w-md rounded-md bg-app-surface shadow-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-app-border flex items-center justify-between bg-app-surface-muted/50">
            <h2 className="text-base font-bold text-app-text">Neuen Kontakt anlegen</h2>
            <button onClick={requestClose} className="p-1 hover:bg-slate-200 rounded">
              <X className="w-4 h-4 text-app-text-subtle" />
            </button>
          </div>
          {formBody}
        </div>
      </div>
      {discardGuard.dialog}
    </>
  );
};
